import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, MailIcon, InstagramIcon, ArrowLeftIcon } from 'lucide-react';
import { KINDS, CateringOffer, VAT_LABEL } from '../data/smaken';
import { usePlatform } from '../contexts/PlatformContext';
import { verdictFor } from '../data/guide';
import { CateringCard } from '../components/CateringCard';
import { Rating } from '../components/Rating';
import { StampBadge } from '../components/StampBadge';
import { MiniMap } from '../components/SmalandMap';
import { PlaceCard } from '../components/PlaceCard';
import { Reveal } from '../components/Reveal';
import { ArrowMark, Note, Stamp } from '../components/Marks';
const ACTION_LABEL: Record<string, string> = {
  boka: 'Boka bord',
  bestall: 'Beställ',
  besok: 'Hitta hit'
};

/** Keeps the partner's own menu order: sections appear as they first occur. */
function groupBySection(offers: CateringOffer[]): [string, CateringOffer[]][] {
  const map = new Map<string, CateringOffer[]>();
  offers.forEach(o => map.set(o.section, [...(map.get(o.section) ?? []), o]));
  return [...map];
}
const slugify = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
export function PlacePage() {
  const {
    slug
  } = useParams();
  const {
    partners,
    cateringFor
  } = usePlatform();
  const published = partners.filter(p => p.published).map(p => p.place);
  const place = published.find(p => p.slug === slug);
  if (!place) {
    return <div className="mx-auto max-w-page px-5 py-32 text-center md:px-8" data-id="element-1040" data-mp="vr9a2jd8lnzb">
        <p className="display-xl text-4xl uppercase" data-id="element-1041" data-mp="10kk5zpgigkcg">Hittade inte hit.</p>
        <Link to="/upptack" className="mt-8 inline-block bg-rod px-8 py-4 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1042" data-mp="15ic9lgqipuwh">
          Tillbaka till Upptäck
        </Link>
      </div>;
  }
  const accent = KINDS.find(k => k.id === place.kind)?.accent ?? '#E42616';
  const related = published.filter(p => p.slug !== place.slug).slice(0, 3);
  const catering = cateringFor(place.slug);
  const verdict = verdictFor(place.slug);
  const sections = groupBySection(catering);
  return <article data-id="element-1043" data-mp="urqwbidw5vl6">
      {/* ------------------------------- Masthead ------------------------------- */}
      <div className="mx-auto max-w-page px-5 pb-8 pt-8 md:px-8 md:pt-10" data-id="element-1044" data-mp="18j5czikbtkp2">
        <Link to="/upptack" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide2 text-black/50 transition-colors duration-150 ease-out hover:text-rod" data-id="element-1045" data-mp="18l8cquba6s6z">
          <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1046" data-mp="1di8kd9ns4307" />
          Upptäck
        </Link>

        <div className="mt-6 grid items-end gap-6 lg:grid-cols-[minmax(0,1.6fr),minmax(0,1fr)]" data-id="element-1047" data-mp="3k8ok6qc0j0qi">
          <div data-id="element-1048" data-mp="3f9ldz2kaqogy">
            {place.stamp && <Stamp className="mb-4" data-id="element-1049" data-mp="3famzy22uovdg">{place.stamp}</Stamp>}
            <h1 className="display-xl text-[3rem] uppercase leading-[0.9] sm:text-[4.5rem]" data-id="element-1050" data-mp="3fbxztqt6diza">{place.name}</h1>
            <p className="mt-4 text-[12px] font-bold uppercase tracking-wide2" style={{
            color: accent
          }} data-id="element-1051" data-mp="1zqnnwcg9n1qa">
              {place.city} · {place.tags.join(' · ')}
            </p>
            {verdict && <div className="mt-5" data-id="element-1052" data-mp="1q0kq7yy78zlm">
                <Rating value={verdict.rating} data-id="element-1053" data-mp="1pzjcfmpstz07" />
                <p className="mt-2 font-hand text-2xl text-rod" data-id="element-1054" data-mp="1c12uezmhkm2r">Beställ: {verdict.order}</p>
              </div>}
          </div>

          <div className="flex flex-wrap gap-3 lg:justify-end" data-id="element-1055" data-mp="1g3ppraid8p4p">
            {catering.length > 0 ? <a href="#catering" className="bg-rod px-7 py-3.5 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-1056" data-mp="123x1rmy84n0f">
                Beställ catering
              </a> : <a href={`mailto:${place.email ?? 'hej@smaken.se'}`} className="bg-black px-7 py-3.5 text-[12px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-rod" data-id="element-1057" data-mp="31ey9eqi0cqvb">
                {ACTION_LABEL[place.action]}
              </a>}
            <a href="#hitta-hit" className="border border-black px-7 py-3.5 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:bg-black hover:text-papper" data-id="element-1058" data-mp="sf69bk2knjtm">
              Visa på karta
            </a>
          </div>
        </div>
      </div>

      {/* -------------------------------- Gallery -------------------------------- */}
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-1059" data-mp="8mq2klamzmx5">
        <div className="relative grid gap-3 sm:grid-cols-[minmax(0,2fr),minmax(0,1fr)]" data-id="element-1060" data-mp="4osczhcyeyt8">
          <StampBadge className="absolute -top-5 left-4 z-10 w-36 sm:-top-7 sm:w-44" data-id="element-1061" data-mp="3vsquwx0gzj5x" />
          {place.image ? <img src={place.image} alt={place.name} className="aspect-[4/3] w-full object-cover sm:aspect-[3/2]" data-id="element-1062" data-mp="3qv7gnx3rv6dz" /> : <div className="aspect-[4/3] w-full bg-black/5 sm:aspect-[3/2]" data-id="element-1063" data-mp="2baoql1unr8i2" />}
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-1" data-id="element-1064" data-mp="2gaktsyvj6vwk">
            {place.gallery.slice(0, 3).map(src => <img key={src} src={src} alt="" loading="lazy" className="aspect-square w-full object-cover sm:aspect-[3/2]" data-id="element-1065" data-mp="2kbw9ocols2rk" />)}
          </div>
        </div>
      </div>

      {/* --------------------------------- Catering -------------------------------- */}
      {catering.length > 0 && <section id="catering" aria-labelledby="catering-rubrik" className="mt-16 border-y-2 border-black" data-id="element-1066" data-mp="21ed7nt0kbken">
          <div className="mx-auto max-w-page px-5 py-14 md:px-8 md:py-16" data-id="element-1067" data-mp="14v5uvpjrhtyt">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-1068" data-mp="18w7p1rytnftx">
              <div data-id="element-1069" data-mp="2c7n2f4dzaq5i">
                <h2 id="catering-rubrik" className="display-xl text-4xl uppercase sm:text-5xl" data-id="element-1070" data-mp="2c7513tcgllqq">
                  Catering från {place.name}
                </h2>
                <Note className="mt-3 block" data-id="element-1071" data-mp="2lxq741ugpsjo">Lägg till i din förfrågan — betala inget nu.</Note>
              </div>
              <Link to="/forfragan" className="group flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:text-rod" data-id="element-1072" data-mp="2gydbarlm5b4y">
                Till förfrågan
                <ArrowMark className="h-4 w-9 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-1073" data-mp="1occ47ei9fzil" />
              </Link>
            </div>

            {/* Section jump links — the menu is long, nobody should scroll blind */}
            <nav aria-label="Menydelar" className="mt-8 flex flex-wrap gap-2" data-id="element-1074" data-mp="3doe6p9ghmpv8">
              {sections.map(([section, items]) => <a key={section} href={`#meny-${slugify(section)}`} className="border border-black/20 px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out hover:border-black" data-id="element-1075" data-mp="39n0yispxaal5">
                  {section} <span className="text-black/40" data-id="element-1076" data-mp="37agr2qdoftmm">{items.length}</span>
                </a>)}
            </nav>

            {sections.map(([section, items]) => <div key={section} id={`meny-${slugify(section)}`} className="mt-14 scroll-mt-32" data-id="element-1077" data-mp="3t8ae9uoqzth">
                <h3 className="display-xl text-3xl uppercase" data-id="element-1078" data-mp="8q8ggkrjc2kl">{section}</h3>
                <div className="mt-6 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1079" data-mp="3q8b5xl1hkzcx">
                  {items.map(offer => <CateringCard key={offer.id} offer={offer} showPlace={false} data-id="element-1080" data-mp="3v5jmjh6zkvc2" />)}
                </div>
              </div>)}

            <p className="mt-12 text-sm text-black/55" data-id="element-1081" data-mp="rhhrwzkv91et">
              Priser inkl. {VAT_LABEL} moms. Öppna "Innehåll & allergener" på varje produkt — saknas
              något, ange specialkost i förfrågan så säkerställer vi med köket.
            </p>
          </div>
        </section>}

      {/* -------------------------------- Editorial ------------------------------- */}
      <div className="mx-auto grid max-w-page gap-12 px-5 py-16 md:px-8 md:py-20 lg:grid-cols-[minmax(0,1.5fr),minmax(0,1fr)] lg:gap-20" data-id="element-1082" data-mp="29thnlo7qb9js">
        <div data-id="element-1083" data-mp="36iqokykqswld">
          <Reveal data-id="element-1084" data-mp="36igzdd7iutgq">
            <section aria-labelledby="om" data-id="element-1085" data-mp="36iz0an54pvuz">
              <h2 id="om" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1086" data-mp="32hn9wfefl6lq">
                Om
              </h2>
              <p className="mt-4 font-display text-2xl leading-snug sm:text-3xl" data-id="element-1087" data-mp="2xhiqagi87vra">{place.blurb}</p>
              {place.description.map(paragraph => <p key={paragraph} className="mt-5 text-lg leading-relaxed text-black/70" data-id="element-1088" data-mp="1m3ne9gmmc34a">
                  {paragraph}
                </p>)}
            </section>
          </Reveal>

          <Reveal delay={0.06} data-id="element-1089" data-mp="1715gi5ng8sql">
            <section aria-labelledby="gillar" className="mt-14 border-t-2 border-black pt-8" data-id="element-1090" data-mp="171yg8mnzqhz8">
              <div className="flex items-baseline gap-3" data-id="element-1091" data-mp="1c11i5y8oc82z">
                <h2 id="gillar" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1092" data-mp="1gz33kptkojhm">
                  Det här gillar vi
                </h2>
                <Note className="!text-lg" data-id="element-1093" data-mp="1lrcqry3az5g7">Vår åsikt, inte deras.</Note>
              </div>
              <ul className="mt-6 space-y-4" data-id="element-1094" data-mp="1pqv86jvx0yea">
                {place.weLike.map(item => <li key={item} className="flex items-start gap-4" data-id="element-1095" data-mp="1up6fonyqmwtc">
                    <ArrowMark className="mt-2 h-4 w-10 shrink-0" color={accent} data-id="element-1096" data-mp="1zp1bn5xdecl9" />
                    <span className="text-lg leading-relaxed" data-id="element-1097" data-mp="24oduzgp191j3">{item}</span>
                  </li>)}
              </ul>
            </section>
          </Reveal>

        </div>

        {/* --------------------------------- Facts --------------------------------- */}
        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1098" data-mp="acqsr93t9yso">
          <Reveal delay={0.06} data-id="element-1099" data-mp="2zgio8gjkg0p0">
            <section id="hitta-hit" aria-labelledby="hitta" className="border-2 border-black" data-id="element-1100" data-mp="2zhacf6au2w59">
              <MiniMap city={place.city} accent={accent} className="w-full border-b-2 border-black" data-id="element-1101" data-mp="34gej9qaachsf" />
              <div className="p-6" data-id="element-1102" data-mp="38i03jwp5fycn">
                <h2 id="hitta" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1103" data-mp="3dfi2qvdd6z7g">
                  Hitta hit
                </h2>
                <p className="mt-3 flex items-start gap-2.5 leading-relaxed" data-id="element-1104" data-mp="3ibz1eji6wvys">
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-black/40" aria-hidden="true" data-id="element-1105" data-mp="3nblemsy0qc7l" />
                  {place.address}
                </p>

                <h2 className="mt-8 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1106" data-mp="3w585q5ra6rag">Öppet</h2>
                <ul className="mt-3 space-y-1.5" data-id="element-1107" data-mp="4zowragnt64n">
                  {place.hours.map(row => <li key={row.day} className="flex justify-between gap-4 text-sm" data-id="element-1108" data-mp="9xqe9qyfsxe0">
                      <span className="text-black/60" data-id="element-1109" data-mp="k1x2ytf4y1aj">{row.day}</span>
                      <span className="font-medium" data-id="element-1110" data-mp="k03t759870yb">{row.time}</span>
                    </li>)}
                </ul>

                {(place.email || place.instagram) && <>
                    <h2 className="mt-8 text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-1111" data-mp="a5u01lcglymj">
                      Kontakt
                    </h2>
                    <ul className="mt-3 space-y-2 text-sm" data-id="element-1112" data-mp="12rd6lpm4a9v6">
                      {place.email && <li className="flex items-center gap-2.5" data-id="element-1113" data-mp="xu4wmcb4hc50">
                          <MailIcon className="h-4 w-4 shrink-0 text-black/40" aria-hidden="true" data-id="element-1114" data-mp="t2d6g2vvp8fe" />
                          <a href={`mailto:${place.email}`} className="transition-colors duration-150 ease-out hover:text-rod" data-id="element-1115" data-mp="p22ywlsfu9yf">
                            {place.email}
                          </a>
                        </li>}
                      {place.instagram && <li className="flex items-center gap-2.5" data-id="element-1116" data-mp="3fz4oexltsd1j">
                          <InstagramIcon className="h-4 w-4 shrink-0 text-black/40" aria-hidden="true" data-id="element-1117" data-mp="3g1h391wae7ad" />
                          {place.instagram}
                        </li>}
                    </ul>
                  </>}
              </div>
            </section>
          </Reveal>
        </aside>
      </div>

      {/* ------------------------------- Discover more ------------------------------ */}
      {related.length > 0 && <section aria-labelledby="mer" className="border-t border-black/12" data-id="element-1118" data-mp="2icxjtnpiukp6">
          <div className="mx-auto max-w-page px-5 py-16 md:px-8 md:py-20" data-id="element-1119" data-mp="2n9xe71d0dz0g">
            <h2 id="mer" className="display-xl text-3xl uppercase sm:text-5xl" data-id="element-1120" data-mp="gk9bizy0t72j">
              Upptäck mer
            </h2>
            <div className="mt-10 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1121" data-mp="ljlzjo0mok6p">
              {related.map((other, i) => <Reveal key={other.slug} delay={Math.min(i * 0.06, 0.18)} data-id="element-1122" data-mp="261xt34jnuxz7" data-mpi={i}>
                  <PlaceCard place={other} data-id="element-1123" data-mp="213cv7brvsqdx" />
                </Reveal>)}
            </div>
          </div>
        </section>}
    </article>;
}